/// <reference types="@types/google.maps" />
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { SharedModule } from '../../_shared/shared.module';
import { FormField } from 'ngx-smart-form/lib/smart-form/classes/form-field';
import {
  FormFieldList,
  SfInputFormField,
  SmartFormComponent,
} from 'ngx-smart-form';
import { FormControl, MaxLengthValidator, Validators } from '@angular/forms';
import { AddressFormData } from './address-form-data';
import { AddressValidator } from './address-validator';
import { AddressErrorsPipe } from './address-errors.pipe';
import { EditableField } from './editable-field';

@Component({
  selector: 'app-address-form',
  standalone: true,
  imports: [SharedModule, AddressErrorsPipe],
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
})
export class AddressFormComponent implements AfterViewInit {
  @ViewChild('placesField') placesField!: ElementRef<HTMLInputElement>;
  @ViewChild(SmartFormComponent)
  smartFormComponent?: SmartFormComponent<AddressFormData>;

  @Input() fieldsToBeShown: FormFieldList<AddressFormData> | undefined;

  @Input() groupName: string = '';

  @Input() set formData(data: Partial<AddressFormData> | undefined) {
    this.fields = this.genFields(data, this.groupName, this.fieldsToBeShown);
  }

  @Input() title = 'Residenza';

  fields: FormField<AddressFormData>[][] = [];

  placesCtrl = new FormControl('', {
    updateOn: 'submit',
  });

  autocomplete!: google.maps.places.Autocomplete;

  fullAddress = '';

  ngAfterViewInit() {
    this.initAutocomplete();
    this.placesCtrl.addValidators([
      AddressValidator(this.smartFormComponent!.form),
    ]);
    this.placesCtrl.updateValueAndValidity();

    if (this.smartFormComponent?.form.value.route) {
      this.placesCtrl.setValue(
        (this.smartFormComponent?.form.value.route
          ? this.smartFormComponent?.form.value.route
          : '') +
          (this.smartFormComponent?.form.value.street_number
            ? ', ' + this.smartFormComponent?.form.value.street_number
            : '') +
          (this.smartFormComponent?.form.value.administrative_area_level_3
            ? ', ' +
              this.smartFormComponent?.form.value.administrative_area_level_3
            : '') +
          (this.smartFormComponent?.form.value.administrative_area_level_2
            ? ', ' +
              this.smartFormComponent?.form.value.administrative_area_level_2
            : '') +
          (this.smartFormComponent?.form.value.country
            ? ', ' + this.smartFormComponent?.form.value.country
            : ''),
        {
          onlySelf: true,
          emitEvent: false,
        }
      );
      this.placesCtrl.updateValueAndValidity();
    }

    this.smartFormComponent?.form.valueChanges.subscribe((val) => {
      this.placesCtrl.updateValueAndValidity({
        onlySelf: true,
        emitEvent: false,
      });
    });

    this.placesCtrl.statusChanges.subscribe((status) => {
      if (status === 'DISABLED') {
        this.smartFormComponent?.form.disable();
      } else {
        if (this.smartFormComponent?.form.disabled) {
          this.smartFormComponent?.form.enable();
        }
      }
    });
  }

  initAutocomplete() {
    // Create the autocomplete object, restricting the search predictions to
    // addresses in the US and Canada.
    this.autocomplete = new google.maps.places.Autocomplete(
      this.placesField.nativeElement,
      {
        //componentRestrictions: { country: ['us', 'ca'] },
        fields: ['address_components', 'geometry'],
        types: ['address'],
      }
    );
    //this.shipAddress.focus();

    // When the user selects an address from the drop-down, populate the
    // address fields in the form.
    this.autocomplete.addListener('place_changed', () => this.fillInAddress());
  }

  fillInAddress() {
    // Get the place details from the autocomplete object.
    const place = this.autocomplete.getPlace();
    let postcode = '';
    this.smartFormComponent?.form.reset();
    // Get each component of the address from the place details,
    // and then fill-in the corresponding field on the form.
    // place.address_components are google.maps.GeocoderAddressComponent objects
    // which are documented at http://goo.gle/3l5i5Mr
    for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
      // @ts-ignore remove once typings fixed
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number': {
          let streetNumberCtrl =
            this.smartFormComponent?.form?.get('street_number');
          streetNumberCtrl?.setValue(component.long_name);
          break;
        }

        case 'route': {
          const route = this.smartFormComponent?.form?.get('route');
          route?.setValue(component.long_name);
          break;
        }

        case 'postal_code': {
          postcode = `${component.long_name}${postcode}`;
          break;
        }

        case 'postal_code_suffix': {
          postcode = `${postcode}-${component.long_name}`;
          break;
        }

        case 'administrative_area_level_3':
          const city = this.smartFormComponent?.form?.get(
            'administrative_area_level_3'
          );
          city?.setValue(component.long_name);
          break;

        case 'country':
          const country = this.smartFormComponent?.form?.get('country');
          country?.setValue(component.long_name);
          break;

        case 'administrative_area_level_2':
          const provincia = this.smartFormComponent?.form?.get(
            'administrative_area_level_2'
          );
          provincia?.setValue(component.short_name);
          break;
      }
    }
    const postalCode = this.smartFormComponent?.form.get('postal_code');

    postalCode?.setValue(postcode);

    // After filling the form with address components from the Autocomplete
    // prediction, set cursor focus on the second address line to encourage
    // entry of subpremise information such as apartment, unit, or floor number.
    //this.address2Field.focus();

    this.placesCtrl.setValue(this.placesField.nativeElement.value, {
      onlySelf: true,
      emitEvent: false,
    });

    this.placesCtrl.updateValueAndValidity();
  }

  private genFields(
    val: Partial<AddressFormData> | undefined,
    groupName: string,
    fieldsToBeShown: FormField<AddressFormData>[][] = [
      [
        new EditableField({
          formControlName: 'country',
          label: 'Stato',
          required: true,
        }),
        new EditableField({
          formControlName: 'administrative_area_level_2',
          label: 'Sigla provincia',
          required: true,
          validators: [Validators.minLength(2), Validators.maxLength(2)],
        }),
      ],
      [
        new EditableField({
          formControlName: 'administrative_area_level_3',
          label: 'Città',
          required: true,
        }),
        new EditableField({
          formControlName: 'route',
          label: 'Indirizzo',
          required: true,
        }),
      ],
      [
        new EditableField({
          formControlName: 'street_number',
          label: 'Numero civico',
          required: true,
        }),
        new EditableField({
          formControlName: 'postal_code',
          label: 'Cap',
          required: true,
        }),
      ],
    ]
  ) {
    fieldsToBeShown = fieldsToBeShown.map((row) => {
      row.forEach((ctrl, i) => {
        ctrl.value =
          ctrl.value !== undefined ? ctrl.value : val?.[ctrl.formControlName];
        ctrl.label = ctrl.label + ' ' + groupName;
      });
      return row;
    });
    return fieldsToBeShown;
  }

  get addressFormValue() {
    let val = { ...(this.smartFormComponent?.formRawValue || {}) };
    Object.entries(val).forEach(
      ([key, value]) => (val[key] = value?.toUpperCase())
    );
    return val;
  }
}
